<template>
    <v-time-picker 
        v-model="model"

        class="time-picker" 
        :class="{ 'dnx-readonly': readonly }"
        input-class="form-control"
        
        format="HH:mm:ss"
        
        :disabled="readonly"
        :hour-range="enabledHourRange"
        :minute-range="enabledMinuteRange"
        :second-range="enabledSecondRange"
        
        hide-clear-button
    />
</template>

<script>
import VTimePicker from 'vue2-timepicker';

//All CSS rules are prefixed - should not cause any conflicts
import 'vue2-timepicker/dist/VueTimepicker.css'

/**
 * Convert the given timestamp object to a value supported by the underlying timepicker component
 * @param value
 * @return {{HH, mm, ss}}
 */
function toLibrarySupportedValue(value) {
    //Numeric values are expected to be represented as strings consisting of 2 numbers, with the first character optionally being a leading zero
    const asPaddedString = maybeNumber => maybeNumber !== undefined ?
        maybeNumber.toString().padStart(2, '0') :
        undefined;

    value = value || {};
    
    return {
        HH: asPaddedString(value.hour),
        mm: asPaddedString(value.minute),
        ss: asPaddedString(value.second)
    }
}

/**
 * Convert an object using the timepicker component's format to a format utilizing numeric values
 * @param value
 * @return {{hour: number, minute: number, second: number}}
 */
function fromLibrarySupportedValue(value) {
    //Our values can be numeric strings, empty strings, or undefined
    //we only care about numeric strings - the rest may be converted to undefined
    const tryParseNumber = maybeNumber => {
        const parsed = parseInt(maybeNumber);
        if(isNaN(parsed)){
            return undefined;
        }
        
        return parsed;
    }
    
    value = value || {};
    
    return {
        hour: tryParseNumber(value.HH),
        minute: tryParseNumber(value.mm),
        second: tryParseNumber(value.ss)
    }
}

export default {
    name: "TimePicker",
    
    components: {
        VTimePicker
    },
    
    props: {
        value: {
            type: Object,
            required: false
        },
        
        disabledTo: {
            type: Object,
            required: false
        },
        
        readonly: {
            type: Boolean,
            required: false
        }
    },
    
    computed: {
        model: {
            get() {
                return toLibrarySupportedValue(this.value);
            },
            set(value) {
                this.$emit('input', fromLibrarySupportedValue(value));
            }
        },
        
        enabledHourRange() {
            const FINAL_HOUR = 23;
            
            if(
                this.disabledTo &&
                this.disabledTo.hour
            ) {
                return [[this.disabledTo.hour, FINAL_HOUR]];
            }
            
            return undefined;
        },
        
        enabledMinuteRange() {
            const FINAL_MINUTE = 59;

            if(
                this.disabledTo &&
                this.value.hour === this.disabledTo.hour &&
                this.disabledTo.minute
            ) {
                return [[this.disabledTo.minute, FINAL_MINUTE]];
            }

            return undefined;
        },
        
        enabledSecondRange() {
            const FINAL_SECOND = 59;

            if(
                this.disabledTo &&
                this.value.hour === this.disabledTo.hour &&
                this.value.minute === this.disabledTo.minute &&
                this.disabledTo.second
            ) {
                return [[this.disabledTo.second, FINAL_SECOND]];
            }

            return undefined;
        }
    }
}
</script>

<style scoped lang="scss">
    .time-picker {
        //Our timepicker makes our text a bit too grey when marked as readonly by default
        //this makes the text hard to read
        //Transform the color of our text to an easier to read shade of grey/black
        &.dnx-readonly {
            ::v-deep {
                .form-control[readonly] {
                    color: #495057;
                }
            }
        }
        
        &:not(.dnx-readonly) {
            //Our timepicker is greyed out by default when `manual-input` is disabled
            //prevent confusion by resetting the background color when our component is not explicitly marked readonly
            ::v-deep {
                .form-control[readonly] {
                    background-color: unset;
                }
            }   
        }
    }
</style>